import React from "react";
import { FolderOpen, FileCheck, FileText, Layers } from "lucide-react";

const defaultStats = {
  total_documents: 0,
  contracts_with_documents: 0,
  unsigned_count: 0,
  signed_count: 0,
};

/**
 * quick: null | "unsigned" | "signed" | "all_contracts"
 * "total" clears quick filter.
 *
 * Visual: Design.md §1.7 (CustomerMiniDashboard).
 */
const ContractDocumentMiniDashboard = ({
  stats = defaultStats,
  loading,
  activeKey = null,
  onCardClick,
}) => {
  const s = { ...defaultStats, ...stats };

  const items = [
    {
      key: "total",
      label: "Tổng hồ sơ",
      shortLabel: "Tổng",
      value: s.total_documents,
      icon: FolderOpen,
      valueClass: "text-rose-900",
      labelClass: "text-rose-800",
      iconWrapClass: "border border-rose-300 bg-white text-rose-800 shadow-sm",
      mobileIconWrap: "bg-rose-200 text-rose-800",
      cardClass:
        "border-rose-300 bg-gradient-to-br from-rose-200 via-rose-100 to-red-50 shadow-rose-200/60",
      blobClass: "bg-rose-400/55",
      watermarkClass: "text-rose-600/40",
      activeRing: "ring-2 ring-rose-400/80",
    },
    {
      key: "all_contracts",
      label: "HĐ có hồ sơ",
      shortLabel: "Hợp đồng",
      value: s.contracts_with_documents,
      icon: FileCheck,
      valueClass: "text-sky-900",
      labelClass: "text-sky-800",
      iconWrapClass: "border border-sky-300 bg-white text-sky-800 shadow-sm",
      mobileIconWrap: "bg-sky-200 text-sky-800",
      cardClass:
        "border-sky-300 bg-gradient-to-br from-sky-200 via-sky-100 to-blue-50 shadow-sky-200/60",
      blobClass: "bg-sky-400/55",
      watermarkClass: "text-sky-600/40",
      activeRing: "ring-2 ring-sky-400/80",
    },
    {
      key: "unsigned",
      label: "Bản chưa ký",
      shortLabel: "Chưa ký",
      value: s.unsigned_count,
      icon: FileText,
      valueClass: "text-amber-900",
      labelClass: "text-amber-800",
      iconWrapClass: "border border-amber-300 bg-white text-amber-800 shadow-sm",
      mobileIconWrap: "bg-amber-200 text-amber-800",
      cardClass:
        "border-amber-300 bg-gradient-to-br from-amber-200 via-amber-100 to-orange-50 shadow-amber-200/60",
      blobClass: "bg-amber-400/55",
      watermarkClass: "text-amber-600/40",
      activeRing: "ring-2 ring-amber-400/80",
    },
    {
      key: "signed",
      label: "Bản đã ký",
      shortLabel: "Đã ký",
      value: s.signed_count,
      icon: Layers,
      valueClass: "text-emerald-900",
      labelClass: "text-emerald-800",
      iconWrapClass:
        "border border-emerald-300 bg-white text-emerald-800 shadow-sm",
      mobileIconWrap: "bg-emerald-200 text-emerald-800",
      cardClass:
        "border-emerald-300 bg-gradient-to-br from-emerald-200 via-emerald-100 to-teal-50 shadow-emerald-200/60",
      blobClass: "bg-emerald-400/55",
      watermarkClass: "text-emerald-600/40",
      activeRing: "ring-2 ring-emerald-400/80",
    },
  ];

  const resolvedActive =
    activeKey == null || activeKey === "" ? "total" : activeKey;

  return (
    <section className="mb-4 md:mb-6" aria-label="Thống kê hồ sơ liên kết">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-[11px] font-bold uppercase tracking-wider text-gray-400 sm:text-xs">
          Tổng quan
        </h2>
        <p className="hidden text-[10px] text-gray-400 md:block">
          Click thẻ để lọc danh sách · click lại để bỏ lọc
        </p>
      </div>

      <div className="grid grid-cols-2 gap-1.5 md:grid-cols-2 md:gap-4 xl:grid-cols-4">
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = resolvedActive === item.key;
          return (
            <button
              key={item.key}
              type="button"
              disabled={loading}
              onClick={() => onCardClick?.(item.key)}
              title={`${item.label} — click để lọc`}
              className={`relative overflow-hidden rounded-xl border p-2 text-left shadow-sm transition-all md:rounded-2xl md:p-4 md:shadow-md ${
                item.cardClass
              } ${isActive ? item.activeRing : ""} ${
                loading ? "cursor-wait opacity-70" : "cursor-pointer hover:shadow-md"
              }`}
            >
              <div
                aria-hidden
                className={`pointer-events-none absolute -right-8 -top-10 hidden h-28 w-28 rounded-full blur-2xl md:block ${item.blobClass}`}
              />
              <Icon
                aria-hidden
                className={`pointer-events-none absolute -bottom-3 -right-2 hidden h-24 w-24 md:block ${item.watermarkClass}`}
                strokeWidth={1.25}
              />

              <div className="relative flex flex-col items-center justify-center gap-0.5 md:hidden">
                <div className={`rounded-lg p-1.5 ${item.mobileIconWrap}`}>
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <p
                  className={`text-base font-black tabular-nums leading-none ${item.valueClass}`}
                >
                  {loading ? "–" : Number(item.value).toLocaleString("vi-VN")}
                </p>
                <p className="truncate text-[9px] font-bold uppercase tracking-wide text-gray-500">
                  {item.shortLabel}
                </p>
              </div>

              <div className="relative z-10 hidden md:block">
                <div className="flex items-start justify-between gap-3">
                  <p
                    className={`min-w-0 truncate text-[11px] font-bold uppercase tracking-wider ${item.labelClass}`}
                  >
                    {item.label}
                  </p>
                  <div className={`shrink-0 rounded-xl p-2 ${item.iconWrapClass}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                </div>
                <p className={`mt-3 text-3xl font-black tabular-nums ${item.valueClass}`}>
                  {loading ? "–" : Number(item.value).toLocaleString("vi-VN")}
                </p>
                {isActive && item.key !== "total" ? (
                  <p className="mt-1 text-[9px] font-bold uppercase tracking-wide text-gray-600">
                    Đang lọc
                  </p>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default ContractDocumentMiniDashboard;
